"use client";

import { useState, useEffect } from "react";

type KeyRow = {
  id: string;
  name: string;
  key_prefix: string;
  last_used_at: string | null;
  created_at: string;
};

export default function MindalooApiKeysPanel({
  textColor,
  textSecondaryColor,
  primaryColor,
  secondaryColor,
}: {
  textColor: string;
  textSecondaryColor: string;
  primaryColor: string;
  secondaryColor: string;
}) {
  const [keys, setKeys] = useState<KeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [createdRawKey, setCreatedRawKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadKeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/keys");
      if (res.status === 401) {
        setKeys([]);
        setError("Sign in to view and create API keys.");
        return;
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load keys");
      setKeys(data.keys || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load keys");
      setKeys([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKeys();
  }, []);

  const createKey = async () => {
    setCreating(true);
    setError(null);
    setCreatedRawKey(null);
    try {
      const res = await fetch("/api/keys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newKeyName.trim() || "Default" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create key. Sign in and ensure the API keys table is set up in Supabase.");
      setCreatedRawKey(data.rawKey);
      setNewKeyName("");
      loadKeys();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create key");
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async (id: string) => {
    if (!confirm("Revoke this API key? Any apps using it will stop working.")) return;
    setError(null);
    try {
      const res = await fetch(`/api/keys/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to revoke key");
      setCreatedRawKey(null);
      loadKeys();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to revoke key");
    }
  };

  const copyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    // Could add a toast; for now user sees the key.
  };

  return (
    <div
      className="p-6 rounded-2xl premium-white-glass backdrop-blur-sm"
      style={{
        border: `1px solid ${primaryColor}20`,
        boxShadow: "0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)",
      }}
    >
      <h3 className="font-bold text-xl mb-1" style={{ color: textColor }}>
        Your AI as an API
      </h3>
      <p className="text-sm mb-4" style={{ color: textSecondaryColor }}>
        The AI you built here can be used <strong>anywhere</strong> you&apos;d normally use ChatGPT, Claude, or Gemini. Generate a key below—then put your base URL + this key into any app, script, or tool. Your custom AI is the backend; no need for someone else&apos;s API. Send your AI&apos;s <strong>profile</strong> in the request body so responses match your AI.
      </p>

      {error && (
        <div className="mb-4">
          <p className="text-sm text-amber-600 dark:text-amber-400 mb-2">{error}</p>
          {(error.includes("migration") || error.includes("not set up") || error.includes("api_keys")) && (
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30">
              <p className="text-sm font-medium mb-1" style={{ color: textColor }}>Setup required</p>
              <p className="text-xs mb-2" style={{ color: textSecondaryColor }}>
                Run the <code className="bg-black/10 dark:bg-white/10 px-1 rounded">api_keys</code> migration in your Supabase project.
              </p>
              <a
                href="/docs"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium underline"
                style={{ color: primaryColor }}
              >
                View setup docs →
              </a>
            </div>
          )}
        </div>
      )}

      {createdRawKey && (
        <div className="mb-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
          <p className="text-sm font-medium mb-2" style={{ color: textColor }}>
            Key created. Copy it now — you won&apos;t see it again.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-sm bg-black/10 dark:bg-white/10 px-2 py-1 rounded break-all">
              {createdRawKey}
            </code>
            <button
              type="button"
              onClick={() => copyKey(createdRawKey)}
              className="px-3 py-1 rounded-lg text-sm font-medium text-white"
              style={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
              }}
            >
              Copy
            </button>
            <button
              type="button"
              onClick={() => setCreatedRawKey(null)}
              className="text-sm opacity-80 hover:opacity-100"
              style={{ color: textSecondaryColor }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input
          type="text"
          placeholder="Key name (e.g. My script)"
          value={newKeyName}
          onChange={(e) => setNewKeyName(e.target.value)}
          className="px-4 py-2 rounded-xl border bg-white/50 dark:bg-black/20 flex-1 min-w-[160px]"
          style={{ color: textColor, borderColor: `${primaryColor}30` }}
        />
        <button
          type="button"
          onClick={createKey}
          disabled={creating}
          className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 disabled:opacity-60 text-sm text-white"
          style={{
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
            boxShadow: `0 4px 16px ${primaryColor}40`,
          }}
        >
          {creating ? "Generating…" : "Generate API key"}
        </button>
      </div>

      {loading ? (
        <p className="text-sm" style={{ color: textSecondaryColor }}>Loading keys…</p>
      ) : keys.length === 0 ? (
        <p className="text-sm" style={{ color: textSecondaryColor }}>No API key yet. Generate one to use your AI in any app that accepts OpenAI, Claude, or Gemini—paste your base URL and this key in place of theirs.</p>
      ) : (
        <ul className="space-y-2">
          {keys.map((k) => (
            <li
              key={k.id}
              className="flex items-center justify-between gap-4 py-2 border-b border-black/5 dark:border-white/5 last:border-0"
            >
              <div>
                <span className="font-medium" style={{ color: textColor }}>{k.name}</span>
                <span className="text-sm ml-2 opacity-70" style={{ color: textSecondaryColor }}>
                  {k.key_prefix}…
                </span>
              </div>
              <button
                type="button"
                onClick={() => revokeKey(k.id)}
                className="text-sm text-red-600 dark:text-red-400 hover:underline"
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs mt-4" style={{ color: textSecondaryColor }}>
        Use your site as base URL (e.g. <code className="bg-black/5 dark:bg-white/5 px-1 rounded">https://Mindaloo.app</code>) and this key as the API key. Endpoints: <code className="bg-black/5 dark:bg-white/5 px-1 rounded">POST /api/ai/run</code>, <code className="bg-black/5 dark:bg-white/5 px-1 rounded">POST /api/v1/chat/completions</code>. Include <code className="bg-black/5 dark:bg-white/5 px-1 rounded">profile</code> in the body. Auth: <code className="bg-black/5 dark:bg-white/5 px-1 rounded">Authorization: Bearer &lt;key&gt;</code> or header <code className="bg-black/5 dark:bg-white/5 px-1 rounded">x-api-key</code>.
      </p>
    </div>
  );
}
